import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/nextjs';
import { IoMdTrash } from 'react-icons/io';
import { MdSave } from 'react-icons/md';
import { Modal as ModalAnt } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import Header from 'legacy/common/components/wysiwyg-form/Header';
import { useEventEditorContext } from 'legacy/events/contexts/EventEditorContext';
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react';
import { ICreatorOptions } from 'survey-creator-core';
import { Serializer, setLicenseKey } from 'survey-core';
import { Theme } from 'theme/survey-js/theme';
import { customThemeVariables } from 'theme/survey-js/creator';
import {
  useCreatePrePurchaseFormMutation,
  useDeletePrePurchaseForm,
  useGetPrePurchaseForm,
  useUpdatePrePurchaseFormMutation
} from 'api/queries/events/pre-purchase-questions';
import Loader from 'legacy/common/components/layout/Loader';
import { error, success } from 'legacy/common/utils/toast';
import Button, { ButtonType } from 'legacy/common/components/button/Button';

import 'survey-creator-core/survey-creator-core.css';

const defaultCreatorOptions: ICreatorOptions = {
  collapseOnDrag: true,
  showCreatorThemeSettings: false,
  showJSONEditorTab: false,
  showThemeTab: false
};

Serializer.removeProperty('survey', 'logo');
Serializer.removeProperty('survey', 'logoFit');
Serializer.removeProperty('survey', 'logoWidth');
Serializer.removeProperty('survey', 'logoHeight');

setLicenseKey(process.env.NEXT_PUBLIC_SURVEYJS);

const customTheme = {
  themeName: 'customTheme',
  cssVariables: { ...customThemeVariables }
};

function PrePurchaseQuestions() {
  const { t } = useTranslation(['events']);
  const {
    isDraft,
    isEditing,
    isLoading: isLoadingEventEditor,
    lastUpdated,
    handleBack,
    navigateToEventSettings,
    eventId,
    event
  } = useEventEditorContext();
  const {
    mutateAsync: createPrePurchaseForm,
    isLoading: isCreatingPrePurchaseForm
  } = useCreatePrePurchaseFormMutation();

  const {
    mutateAsync: updatePrePurchaseForm,
    isLoading: isUpdatingPrePurchaseForm
  } = useUpdatePrePurchaseFormMutation();

  const {
    mutate: deletePrePurchaseForm,
    isLoading: isDeletingPrePurchaseForm
  } = useDeletePrePurchaseForm();

  const { data: prePurchaseForm, isLoading: isLoadingPrePurchaseForm } =
    useGetPrePurchaseForm({ eventId }, { enabled: !!event?.prePurchaseFormId });

  const isLoading =
    isLoadingEventEditor ||
    isLoadingPrePurchaseForm ||
    isCreatingPrePurchaseForm ||
    isUpdatingPrePurchaseForm ||
    isDeletingPrePurchaseForm;

  const handleSaveSurvey = async () => {
    if (Object.keys(creator.JSON).length > 0) {
      try {
        const mutationData = {
          eventId,
          schema: creator.JSON
        };

        if (event?.prePurchaseFormId) {
          await updatePrePurchaseForm(mutationData);
        } else {
          await createPrePurchaseForm(mutationData);
        }

        success(t('events:settings.guestQuestion.saveSuccess'));
        handleBack();
      } catch (err) {
        error(err.message || 'Error saving survey');
        Sentry.captureException(err, {
          tags: {
            component: 'EventPrePurchaseQuestions',
            action: 'saveSurvey'
          }
        });
      }
    }
  };

  const submitSurvey = () => {
    creator.saveSurvey();
  };

  const deleteSurvey = () => {
    ModalAnt.confirm({
      title: t('events:settings.guestQuestion.deleteQuestionsTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('events:settings.guestQuestion.deleteQuestionsConfirmation'),
      centered: true,
      okText: t('events:settings.guestQuestion.deleteQuestionsYes'),
      okType: 'danger',
      cancelText: t('events:settings.guestQuestion.deleteQuestionsNo'),
      onOk: () => {
        try {
          deletePrePurchaseForm(eventId, {
            onSuccess: () => {
              handleBack();
              success(
                t('events:settings.guestQuestion.deleteQuestionsSuccess')
              );
            }
          });
        } catch (err) {
          error(err.message || 'Error deleting pre purchase form');
          Sentry.captureException(err, {
            tags: {
              component: 'EventPrePurchaseQuestions',
              action: 'deleteSurvey'
            }
          });
        }
      }
    });
  };

  const creator = useMemo(() => {
    const creator = new SurveyCreator(defaultCreatorOptions);
    // @ts-expect-error The SurveyJS type here is incorrect. This method is supported and documented in the SurveyJS docs.
    creator.initSurveyWithJSON(prePurchaseForm?.schema, true);
    creator.showSaveButton = false;
    creator.applyCreatorTheme(customTheme);
    creator.applyTheme(Theme);

    const saveActionToolbarIndex = creator.toolbar.actions.findIndex(
      (action) => action.id === 'svd-save'
    );

    const saveActionFooterToolbarIndex =
      creator.footerToolbar.actions.findIndex(
        (action) => action.id === 'svd-save'
      );

    if (saveActionToolbarIndex !== -1) {
      creator.toolbar.actions.splice(saveActionToolbarIndex, 1);
    }
    if (saveActionFooterToolbarIndex !== -1) {
      creator.footerToolbar.actions.splice(saveActionFooterToolbarIndex, 1);
    }

    creator.toolbox.removeItem('file');
    creator.toolbox.removeItem('imagepicker');
    creator.toolbox.removeItem('image');
    creator.toolbox.removeItem('html');
    creator.toolbox.removeItem('surveylogo');

    return creator;
  }, [prePurchaseForm?.schema]);

  // This is a hack to ensure the saveSurveyFunc is recomputed and set when the eventId changes.
  // This is because the creator is not reinitialized when the eventId changes.
  useEffect(() => {
    creator.saveSurveyFunc = handleSaveSurvey;
  }, [creator, eventId]);

  return (
    <>
      <Header
        isDraft={isDraft}
        isEditing={isEditing}
        title={t('events:settings.guestQuestion.title')}
        isLoading={isLoading}
        lastUpdated={lastUpdated}
        onBack={handleBack}
        onMenuClick={navigateToEventSettings}
        className="z-1001"
      />
      <div className="w-full pt-[36x] md:pt-[84px] -mt-[36px] pb-20 h-[calc(100vh-101px)] md:h-[calc(100vh-70px)] lg:h-[calc(100vh-42px)]">
        <SurveyCreatorComponent creator={creator} />
        {isLoading && (
          <div className="absolute top-0 left-0 right-0 bottom-0 z-99 bg-white/80">
            <Loader isLoading />
          </div>
        )}
      </div>
      <div className="w-full bg-white fixed bottom-0 left-0 p-4 z-1000 border-t pb-12 md:pb-4">
        <div className="w-full md:w-[800px] mx-auto flex gap-4 justify-center items-center">
          <Button
            type={ButtonType.Inverted}
            onClick={submitSurvey}
            disabled={isLoading}
            newRadius
            autoWidth={!event?.prePurchaseFormId}
            loading={isLoading}
            icon={<MdSave size={20} />}
            className="w-full md:w-auto"
          >
            <div className="flex items-center justify-center">
              {t(
                `events:settings.guestQuestion.${
                  event?.prePurchaseFormId ? 'updateButton' : 'createButton'
                }`
              )}
            </div>
          </Button>
          {event?.prePurchaseFormId && (
            <Button
              type={ButtonType.Danger}
              onClick={deleteSurvey}
              icon={<IoMdTrash size={20} />}
              mobileRounded
            >
              <span className="hidden md:block">
                {t(`events:settings.guestQuestion.deleteButton`)}
              </span>
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

export default PrePurchaseQuestions;
